import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Card, Col, Form, Row, Space, Input, Upload, Button } from 'antd';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import styles from './../default/default-configuration.module.scss';
import { useMedia } from 'common/hooks/use-media/use-media.hook';
import { ISystemConfigurationUpdate } from 'common/services/configuration-service/configuration.service.types';
import configurationService from 'common/services/configuration-service/configuration.service';
import configurationStore from 'common/stores/configuration/configuration.store';
import { notification } from 'common/utils/notification';
import TextArea from 'antd/es/input/TextArea';
import { MAX_FILE_SIZE_BYTES } from 'common/constans/common.constants';
import previewImage from 'assets/icons/preveiw-image.png';
import { PlusOutlined } from '@ant-design/icons';
import FileService from 'common/services/file-service/file.service';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import featureStore from 'common/stores/features/feature.store';

const CommunityRule = observer(() => {
  const [form] = Form.useForm();
  const [speedLimitImage, setSpeedLimitImage] = useState<Blob | null>();
  const [speedLimitPreviewImage, setSpeedLimitPreviewImage] = useState<string>('');

  const initData = configurationStore.configuration?.default;

  let timeoutId: any;

  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      form.submit();
    }, 1000);
  };

  const onFinish = async (values: any) => {
    if (speedLimitImage) {
      const photoUrl = await FileService.uploadPhoto(speedLimitImage);
      values.speedLimitImageUrl = photoUrl.absoluteUri;
    }
    if (!speedLimitImage && !speedLimitPreviewImage) {
      values.speedLimitImageUrl = '';
    }

    const model: ISystemConfigurationUpdate = {
      default: {
        communityRules: values.communityRules,
        speedLimit: {
          value: values.speedLimit !== '' && values.speedLimit ? values.speedLimit : null,
        },
        speedLimitImageUrl: values.speedLimitImageUrl,
        entryType: values.entryType,
        eiqPassportInstractionText: values.eiqPassportInstractionText,
        eiqPassportShareMessage: values.eiqPassportShareMessage,
      },
      serviceHours: null,
      citations: null,
      guestLimitations: null,
      cardAccessConfiguration: null,
    };

    const result = await configurationService.updateSystemConfiguration(model);
    configurationStore.setConfiguration(result);
    notification.success({
      message: 'Configurations successfully updated',
      duration: 5,
    });
  };

  useEffect(() => {
    if (form) {
      form.setFieldsValue(initData);
    }
    if (initData?.speedLimitImageUrl) {
      setSpeedLimitPreviewImage(initData?.speedLimitImageUrl);
    } else {
      setSpeedLimitPreviewImage('');
      setSpeedLimitImage(null);
    }
  }, [form, initData]);

  const uploadProps = {
    beforeUpload: async (file: Blob) => {
      if (file.size >= MAX_FILE_SIZE_BYTES) {
        notification.error({ message: 'The image size must be less than 5 MB!' });
      } else {
        setSpeedLimitImage(file);
        try {
          const dataURL = await readImageFile(file);
          setSpeedLimitPreviewImage(dataURL);
          handleFormValuesChange(null, null);
        } catch (error) {
          console.error('Error reading file');
        }
      }
      return false;
    },
    maxCount: 1,
    accept: 'image/png, image/jpeg, image/jpg',
    showUploadList: false,
  };

  const readImageFile = (file: Blob) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  };

  const onRemoveSpeedLimitImage = () => {
    setSpeedLimitImage(null);
    setSpeedLimitPreviewImage('');
    handleFormValuesChange(null, null);
  };

  return (
    <FormWrapper form={form} onFinish={onFinish} onValuesChange={handleFormValuesChange} initialValues={initData} layout="vertical">
      <Card className="eiq-card eiq-card-border eiq-configuration">
        <Row className="eiq-card-header">
          <Col span={24}>
            <h1 className="h1">Community Rules and Speed Limit</h1>
            <span className={`${styles.description}`}>
              Configure the instructions, community rules, and speed limit displayed on visitor QR passes and printed visitor passes.
            </span>
          </Col>
        </Row>
        <Row>
          <Col xs={24} sm={12}>
            <Space className={`full-width ${styles.communityRules}`}>
              {featureStore.isFastAccessEnabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span className={styles.label}>QR Code Instructions</span>
                  <span className={`${styles.italic} ${styles.description}`}>
                    Displayed on the QR pass screen for residents and visitors.
                  </span>
                  <Form.Item
                    style={{ marginTop: '10px' }}
                    name="eiqPassportInstractionText"
                    rules={[{ type: 'string', max: 500, message: 'Maximum length 500 characters' }]}>
                    <TextArea showCount maxLength={500} className={styles.eiqTextArea} placeholder="Enter QR code instructions." />
                  </Form.Item>
                </div>
              )}

              {featureStore.isFastAccessEnabled && (
                <div style={{ display: 'flex', flexDirection: 'column', marginTop: '16px', gap: 8 }}>
                  <span className={styles.label}>QR Code Message</span>
                  <span className={`${styles.italic} ${styles.description}`}>
                    Included in the SMS and email sent when a QR pass is shared.
                  </span>
                  <Form.Item
                    style={{ marginTop: '10px' }}
                    name="eiqPassportShareMessage"
                    rules={[{ type: 'string', max: 100, message: 'Maximum length 100 characters' }]}>
                    <TextArea maxLength={100} showCount className={styles.eiqTextArea} placeholder="Enter the message." />
                  </Form.Item>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', marginTop: '16px', gap: 8 }}>
                <span className={styles.label}>Speed Limit</span>
                <span className={`${styles.italic} ${styles.description}`}>Displayed on visitor QR passes and printed visitor passes.</span>
                <Form.Item style={{ marginTop: '10px' }} name="speedLimit">
                  <Input type="number" min={0} max={300} placeholder="Enter speed limit" />
                </Form.Item>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', marginTop: '16px', gap: 8 }}>
                <span className={styles.label}>Speed Limit Image</span>
                <Form.Item>
                  <div className={styles.speedLimitFormItem}>
                    <div>
                      <Upload {...uploadProps} className={styles.photoContainer}>
                        {speedLimitPreviewImage ? (
                          <img className={styles.petImage} src={speedLimitPreviewImage} alt="speed limit img" />
                        ) : (
                          <img className={styles.petImage} src={previewImage} alt="speed limit img" />
                        )}
                        {!speedLimitImage && !speedLimitPreviewImage && (
                          <Button style={{ width: '96px' }} className="secondary-btn" icon={<PlusOutlined />} size="middle">
                            Add
                          </Button>
                        )}
                      </Upload>
                    </div>
                    {(speedLimitPreviewImage || speedLimitImage) && (
                      <Button
                        onClick={onRemoveSpeedLimitImage}
                        style={{ width: '96px' }}
                        className={`secondary-btn ${styles.remove}`}
                        icon={<Trash />}
                        size="middle">
                        Remove
                      </Button>
                    )}
                  </div>
                </Form.Item>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', marginTop: '16px', gap: 8 }}>
                <span className={styles.label}>Community Rules</span>
                <span className={`${styles.italic} ${styles.description}`}>Displayed on visitor QR passes and printed visitor passes.</span>
                <Form.Item
                  style={{ marginTop: '10px' }}
                  name="communityRules"
                  rules={[{ type: 'string', max: 400, message: 'Maximum length 400 characters' }]}>
                  <TextArea maxLength={400} showCount className={styles.eiqTextArea} placeholder="Enter community rules." />
                </Form.Item>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>
    </FormWrapper>
  );
});

export default CommunityRule;
